import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, BrowserRouter, Routes } from 'react-router-dom';
import { AuthProvider, BreadcrumbProvider, Breadcrumbs, SkipToContentLink, lazyNamed, lazyRoute } from '@monecolier/ui';
import { apiClient } from './services/api';
import { Sidebar } from './components/Sidebar';
import { PushNotificationRegistration } from './components/PushNotificationRegistration';
import { buildBreadcrumbRoutes } from './breadcrumbs';

/*
 * Découpage de code par route (audit Impeccable, 2026-09-07) — MÊME décision
 * que web-admin, voir apps/web-admin/src/App.tsx pour la justification
 * complète (chunk unique de 457,23 kB / 132,39 kB gzip avant ce changement
 * côté parent).
 *
 * Restent STATIQUES : les écrans atteignables sans authentification, donc sur
 * le chemin du premier rendu (Home/Login/Register/ForgotPassword/
 * ResetPassword/NotFound) et les deux pages d'acceptation d'invitation
 * (AcceptGuardianInvitation/AcceptParentInvitation) — un parent y arrive par
 * un lien reçu par email ou WhatsApp, c'est-à-dire en tout premier écran de sa
 * session, souvent depuis un téléphone sur une connexion inégale. Les rendre
 * paresseuses ajouterait un aller-retour réseau juste avant l'action que
 * l'école attend de lui.
 */
import { Home } from './pages/Home';
import { AcceptGuardianInvitation } from './pages/AcceptGuardianInvitation';
import { AcceptParentInvitation } from './pages/AcceptParentInvitation';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { NotFound } from './pages/NotFound';

const Inscriptions = lazyNamed(() => import('./pages/Inscriptions'), 'Inscriptions');
const DeclareChild = lazyNamed(() => import('./pages/DeclareChild'), 'DeclareChild');
const DeclaredChildGuardians = lazyNamed(() => import('./pages/DeclaredChildGuardians'), 'DeclaredChildGuardians');
const EnrollmentDocuments = lazyNamed(() => import('./pages/EnrollmentDocuments'), 'EnrollmentDocuments');
const StudentFinancialOverview = lazyNamed(
  () => import('./pages/StudentFinancialOverview'),
  'StudentFinancialOverview',
);
const Payments = lazyNamed(() => import('./pages/Payments'), 'Payments');
const Attendance = lazyNamed(() => import('./pages/Attendance'), 'Attendance');
const Grades = lazyNamed(() => import('./pages/Grades'), 'Grades');
const Documents = lazyNamed(() => import('./pages/Documents'), 'Documents');
const Settings = lazyNamed(() => import('./pages/Settings'), 'Settings');
const Profile = lazyNamed(() => import('./pages/Profile'), 'Profile');
const NotificationPreferences = lazyNamed(() => import('./pages/NotificationPreferences'), 'NotificationPreferences');
const DataRequests = lazyNamed(() => import('./pages/DataRequests'), 'DataRequests');
const ChangePassword = lazyNamed(() => import('./pages/ChangePassword'), 'ChangePassword');

export function App() {
  // `useTranslation` ici et non une chaîne en dur : contrairement à web-admin,
  // cette app est bilingue (voir src/i18n) et le lien d'évitement est du texte
  // lu par l'utilisateur comme n'importe quel autre.
  const { t } = useTranslation();
  // UX-85 : config du fil d'Ariane reconstruite quand la langue change (`t`
  // change d'identité avec elle), jamais à chaque rendu.
  const breadcrumbRoutes = useMemo(() => buildBreadcrumbRoutes(t), [t]);

  return (
    <AuthProvider apiClient={apiClient}>
      {/* MOB-06 : voir le commentaire équivalent dans apps/web-admin/src/App.tsx. */}
      <PushNotificationRegistration />
      {/* FND-23 : voir le commentaire équivalent dans apps/web-admin/src/App.tsx. */}
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        {/* UX-17 : patron de navigation UNIFIÉ avec web-admin — `<Sidebar />`
            montée UNE SEULE FOIS, persiste sur toute navigation (jamais
            démontée/remontée), même précédent que web-admin (UX-09). Se
            masque elle-même (groupes vides) tant que non authentifié — voir
            components/Sidebar.tsx. `#root` (index.css) devient une rangée
            (Sidebar + contenu) pour l'accueillir, même mise en page que
            web-admin. */}
        {/* Lien d'évitement (audit Impeccable, 2026-09-07) — PREMIER élément
            focusable du document, donc AVANT <Sidebar /> : placé après, il
            n'éviterait plus rien. Voir packages/ui/src/SkipToContentLink.tsx
            (WCAG 2.1 A, 2.4.1 Bypass Blocks). */}
        <SkipToContentLink label={t('a11y.skipToContent')} />
        <Sidebar />
        {/* UX-55 : `min-w-0` — voir le commentaire équivalent dans
            apps/web-admin/src/App.tsx (même correctif, même raison : `<main>`
            est un enfant flex de `#root`/`lg:flex-row`, sans `min-w-0` il
            refuse de rétrécir sous la largeur minimale de son contenu). */}
        <main id="contenu-principal" className="flex min-w-0 flex-1 flex-col items-center justify-start px-2 py-6 sm:px-4 sm:py-10">
          {/* UX-85 : fil d'Ariane, monté UNE SEULE FOIS juste au-dessus de
              <Routes> — voir breadcrumbs.ts et packages/ui/src/Breadcrumbs.tsx. */}
          <BreadcrumbProvider routes={breadcrumbRoutes}>
            <Breadcrumbs ariaLabel={t('breadcrumbs.ariaLabel')} />
            <Routes>
              <Route path="/" element={<Home />} />
              {/* UX-38 : `Children.tsx`/`Enroll.tsx`/`Reenroll.tsx` FUSIONNÉES en une
                  seule page de gestion complète — voir Inscriptions.tsx pour le
                  détail de la décision (Option B) et de la réutilisation de chaque
                  logique de fond. */}
              <Route path="/inscriptions" element={lazyRoute(<Inscriptions />)} />
              {/* Anciennes routes CONSERVÉES en redirection (jamais un 404), même
                  précédent que /supplies ci-dessous — pour tout lien profond déjà
                  en circulation (bookmark, historique navigateur). */}
              <Route path="/children" element={<Navigate to="/inscriptions" replace />} />
              <Route path="/enroll" element={<Navigate to="/inscriptions" replace />} />
              <Route path="/reenroll" element={<Navigate to="/inscriptions" replace />} />
              {/* PAR-13 : vue DÉDIÉE à la déclaration d'un enfant. Segment statique
                  "new" — distinct de "/children/:declaredChildId/guardians"
                  ci-dessous (React Router v6 matche par spécificité, pas par ordre
                  d'enregistrement comme Express : aucune collision possible avec
                  la redirection générique "/children" ci-dessus). */}
              <Route path="/children/new" element={lazyRoute(<DeclareChild />)} />
              <Route path="/children/:declaredChildId/guardians" element={lazyRoute(<DeclaredChildGuardians />)} />
              <Route path="/accept-guardian-invitation" element={<AcceptGuardianInvitation />} />
              {/* AUT-18 : invitation envoyée par l'ÉCOLE (élève importé en masse),
                  distincte de /accept-guardian-invitation (co-parent invité par un
                  autre parent, PAR-04) — deux flux, deux jetons, deux tables. */}
              <Route path="/accept-parent-invitation" element={<AcceptParentInvitation />} />
              <Route path="/enrollments/:enrollmentId/documents" element={lazyRoute(<EnrollmentDocuments />)} />
              {/* UX-26 : "Fournitures scolaires" (FOU-02, `Supplies.tsx`) fusionnée
                  dans la page "Paiements" unique — une ligne d'échéance parmi
                  les autres, plus une page/entrée de menu séparée. Route
                  CONSERVÉE en redirection (jamais un 404), même précédent que
                  UX-09 (`/reenrollment-status`)/UX-15 (`/supply-catalog`)/UX-16
                  (`/supply-payments`, `/combined-payments`) pour tout lien
                  profond déjà en circulation (bookmark, historique navigateur —
                  aucun lien EXTERNE trouvé pointant vers `/supplies`, vérifié
                  par grep AVANT ce ticket, mais un lien interne n'est jamais la
                  seule source possible). */}
              <Route path="/supplies" element={<Navigate to="/payments" replace />} />
              <Route path="/students/:studentId/financial-overview" element={lazyRoute(<StudentFinancialOverview />)} />
              {/* UX-17 : "Paiements"/"Documents" sont désormais des groupes de la
                  Sidebar (voir components/Sidebar.tsx) plutôt que des onglets
                  (UX-11, BottomTabBar retiré). Contenu de ces deux pages
                  INCHANGÉ — seule leur intégration à la navigation change. */}
              <Route path="/payments" element={lazyRoute(<Payments />)} />
              {/* ATT-03 : nouvel onglet DÉDIÉ "Présences" — lecture seule, voir
                  components/Sidebar.tsx pour sa position dans la navigation. */}
              <Route path="/attendance" element={lazyRoute(<Attendance />)} />
              <Route path="/grades" element={lazyRoute(<Grades />)} />
              <Route path="/documents" element={lazyRoute(<Documents />)} />
              {/* UX-27 : "Réglages" — hub qui mène vers les routes ci-dessous. AUT-16 :
                  "Changer mon mot de passe" ajoutée à ce même hub (tuile
                  dédiée, Settings.tsx) — emplacement confirmé par le PO. */}
              <Route path="/settings" element={lazyRoute(<Settings />)} />
              {/* PAR-14 : "Mes informations personnelles" — même hub Réglages,
                  nom DISTINCT de "Mes données personnelles" (RGPD, /data-requests
                  ci-dessous) pour éviter toute confusion entre les deux. */}
              <Route path="/profile" element={lazyRoute(<Profile />)} />
              <Route path="/notification-preferences" element={lazyRoute(<NotificationPreferences />)} />
              <Route path="/data-requests" element={lazyRoute(<DataRequests />)} />
              <Route path="/change-password" element={lazyRoute(<ChangePassword />)} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BreadcrumbProvider>
        </main>
      </BrowserRouter>
    </AuthProvider>
  );
}
